// Batch 29 · M65 — one item, one misstatement. A worksheet send of an item that is already in Misstatements from another
// worksheet comes back 409 MISSTATEMENT_DUPLICATE naming the existing row; the screen then offers "Already in Misstatements –
// link": the source row is linked to that row (POST …/misstatements/link) and counts as sent — no second row is created.
// Manual / observation misstatements only WARN ("This may be a duplicate of … – please check") and can be saved anyway.
import api from "@/lib/api";
import { askConfirm } from "@/lib/appDialog";
import { toast } from "sonner";

export const MSD_TEXT = {
  en: {
    title: "Already in Misstatements",
    body: "Already in Misstatements: {row}. This item was sent from another worksheet. Link this row to it instead of sending it again?",
    link: "Already in Misstatements – link",
    linked: "Linked to the existing misstatement — this row now counts as sent.",
    possibleTitle: "Possible duplicate",
    possible: "This may be a duplicate of {row} – please check.",
    saveAnyway: "Save anyway",
    fsHead: "FS head",
  },
  ar: {
    title: "موجود مسبقاً في التحريفات",
    body: "موجود مسبقاً في التحريفات: {row}. أُرسل هذا البند من ورقة عمل أخرى. هل تربط هذا الصف به بدلاً من إرساله مرة أخرى؟",
    link: "موجود مسبقاً في التحريفات – ربط",
    linked: "رُبط بالتحريف الموجود — يُعد هذا الصف الآن مُرسلاً.",
    possibleTitle: "تكرار محتمل",
    possible: "قد يكون هذا تكراراً لـ {row} – يرجى التحقق.",
    saveAnyway: "احفظ على أي حال",
    fsHead: "البند",
  },
};

const L = (lang) => MSD_TEXT[lang === "ar" ? "ar" : "en"];

export function isDuplicateError(e) {
  return e?.response?.status === 409 && e?.response?.data?.error_code === "MISSTATEMENT_DUPLICATE";
}

/** "FS head 3100 · 60,214.00 · Cut-off worksheet (post-year-end row) · <description or ref>" */
export function describeRow(d, lang = "en") {
  if (!d) return "";
  const amt = Number(d.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const src = lang === "ar" ? (d.source_ar || d.source) : d.source;
  return `${L(lang).fsHead} ${d.fs_head || "—"} · ${amt} · ${src || ""} · ${d.description || d.ref || ""}`;
}

/** On a 409 MISSTATEMENT_DUPLICATE: offer the link. Returns "linked", "declined", or "" (not a duplicate error).
 *  target (optional) tells the server which worksheet row keeps the misstatement id: {type: "surl_row" | "surl_journal" |
 *  "cutoff_row" | "cutoff_pye", code, row_id | entry_id | index}. */
export async function offerLink(e, { engId, target = null, lang = "en" }) {
  if (!isDuplicateError(e)) return "";
  const data = e.response.data || {};
  const T = L(lang);
  const ok = await askConfirm(T.body.replace("{row}", describeRow(data.existing, lang)), { title: T.title, okLabel: T.link });
  if (!ok) return "declined";
  await api.post(`/engagements/${engId}/misstatements/link`, {
    source_kind: data.blocked?.source_kind, source_id: data.blocked?.source_id,
    misstatement_id: data.existing?.id, target: target || undefined,
  });
  toast.success(T.linked);
  return "linked";
}

/** Manual / observation misstatements: true to go on saving (no possible duplicate, or the user chose "Save anyway"). */
export async function confirmPossibleDuplicate({ engId, fsHead, amount, lang = "en" }) {
  let rows = [];
  try {
    const r = await api.get(`/engagements/${engId}/misstatements/possible-duplicates`,
      { params: { fs_head: fsHead || "", amount: Number(amount || 0) }, _silent: true });
    rows = Array.isArray(r?.data?.possible_duplicates) ? r.data.possible_duplicates : [];
  } catch (e) { return true; }      // the check never stops a save
  if (!rows.length) return true;
  const T = L(lang);
  return askConfirm(T.possible.replace("{row}", describeRow(rows[0], lang)), { title: T.possibleTitle, okLabel: T.saveAnyway });
}
